import React, { useState, useEffect, useRef } from "react";

import { mixpanel } from "../../utils/mixpanel";

/**
 * WAGS Conference Landing Page React Component
 *
 * Features comprehensive Mixpanel tracking for:
 * - Page views and session management
 * - User interactions (clicks, hovers, scrolling)
 * - Video engagement and conversion funnel
 * - Form submissions and CTA performance
 * - Detailed user behavior analytics
 *
 * All events include rich context like device type, timing, user session,
 * clinic information, and conversion funnel position.
 */

const WAGSConferenceLanding = ({
  videoUrl = "https://player.vimeo.com/video/YOUR_VIDEO_ID",
  onSignUpClick = () =>
    window.open("https://dashboard.clinicos.ai/signup", "_blank"),
  onLoginClick = () =>
    window.open("https://dashboard.clinicos.ai/signin", "_blank"),
  showHeader = true,
  className = "",
  clinicName = null, // Pass clinic name for tracking
  userId = null, // Pass user ID for tracking
  referralSource = "direct", // Track how users arrived
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [stats, setStats] = useState({
    professionals: 500,
    procedures: 20,
    discount: 50,
  });

  const pageLoadTime = useRef(Date.now());
  const sessionId = useRef(
    `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  );

  // Design system colors
  const colors = {
    primary: "#00B7F4",
    primaryDark: "#0099CC",
    background: "#FFFFFF",
    cardBg: "#f9f9f9",
    lightBlueBg: "#F1FAFF",
    accentBg: "#EAF6FF",
    title: "#111",
    text: "#444",
    subtext: "#555",
    border: "#E5E5E5",
    success: "#22C55E",
    warning: "#F97316",
  };

  // Initialize tracking and session
  useEffect(() => {
    // Track initial page view with comprehensive properties
    trackEvent("WAGS Conference Page Viewed", {
      "Page Type": "Landing",
      Source: referralSource,
      "Entry Point": document.referrer || "Direct",
      "User Agent": navigator.userAgent,
      "Device Type": window.innerWidth < 768 ? "Mobile" : "Desktop",
      "Screen Width": window.innerWidth,
      "Screen Height": window.innerHeight,
      "Session ID": sessionId.current,
      URL: window.location.href,
    });

    // Track session start
    trackEvent("WAGS Conference Session Started", {
      "Referral Source": referralSource,
      "Landing URL": window.location.href,
      "Device Type": window.innerWidth < 768 ? "Mobile" : "Desktop",
      "Clinic Name": clinicName ?? "clinic not found",
      "User ID": userId ?? "anonymous",
      "Session ID": sessionId.current,
    });

    return () => {
      const timeSpent = Math.round((Date.now() - pageLoadTime.current) / 1000);
      trackEvent("WAGS Conference Session Ended", {
        "Time Spent Seconds": timeSpent,
        "Session ID": sessionId.current,
        "Clinic Name": clinicName ?? "clinic not found",
        "User ID": userId ?? "anonymous",
      });
    };
  }, [referralSource, clinicName, userId]);

  // Countdown timer
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextTuesday = new Date();

      const daysUntilTuesday = (2 - now.getDay() + 7) % 7;
      if (daysUntilTuesday === 0 && now.getHours() >= 23) {
        nextTuesday.setDate(now.getDate() + 7);
      } else if (daysUntilTuesday === 0) {
        nextTuesday.setHours(23, 59, 59, 999);
      } else {
        nextTuesday.setDate(now.getDate() + daysUntilTuesday);
        nextTuesday.setHours(23, 59, 59, 999);
      }

      const distance = nextTuesday.getTime() - now.getTime();

      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          ),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  // Scroll depth tracking
  useEffect(() => {
    let maxScroll = 0;
    let tracked = { 25: false, 50: false, 75: false, 100: false };
    let scrollStartTime = Date.now();
    let lastScrollTime = Date.now();

    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight)) *
          100,
      );
      const currentTime = Date.now();

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
      }

      for (let milestone in tracked) {
        if (scrollPercent >= milestone && !tracked[milestone]) {
          tracked[milestone] = true;
          trackEvent("WAGS Conference Scroll Depth Reached", {
            "Depth Percentage": milestone,
            "Max Scroll": maxScroll,
            "Time to Reach": Math.round((currentTime - scrollStartTime) / 1000),
            "Scroll Speed":
              scrollPercent / ((currentTime - scrollStartTime) / 1000),
            "Time Since Last Milestone": Math.round(
              (currentTime - lastScrollTime) / 1000,
            ),
          });
          lastScrollTime = currentTime;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const trackEvent = (eventName, additionalProperties = {}) => {
    const baseProperties = {
      Page: "WAGS Conference Landing",
      Timestamp: new Date().toISOString(),
      URL: window.location.href,
      "User Agent": navigator.userAgent,
      "Device Type": window.innerWidth < 768 ? "Mobile" : "Desktop",
      "Screen Width": window.innerWidth,
      "Screen Height": window.innerHeight,
      "Session ID": sessionId.current,
      "Clinic Name": clinicName ?? "clinic not found",
      "User ID": userId ?? "anonymous",
      "Referral Source": referralSource,
      "Browser Language": navigator.language,
      Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      "Local Time": new Date().toLocaleString(),
      ...additionalProperties,
    };

    console.log("Tracking event:", eventName, baseProperties);
    mixpanel.track(eventName, baseProperties);
  };

  const playVideo = () => {
    setIsVideoPlaying(true);
    trackEvent("WAGS Conference Video Started", {
      "Video Name": "WAGS Conference Demo",
      "Video URL": videoUrl,
      Location: "Hero Section",
      "Play Method": "Click",
      "Video Duration": "3:45",
      "Player Type": "Embedded",
      "Auto Play": false,
      "Time on Page Before Play": Math.round(
        (Date.now() - pageLoadTime.current) / 1000,
      ),
      "Previous Scroll Depth": Math.round(
        (window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight)) *
          100,
      ),
    });
  };

  const copyPromoCode = () => {
    navigator.clipboard.writeText("WAGS50").then(() => {
      trackEvent("WAGS Conference Promo Code Copied", {
        "Promo Code": "WAGS50",
        "Copy Method": "Click",
        "Discount Percentage": 50,
        "Time on Page": Math.round((Date.now() - pageLoadTime.current) / 1000),
        "Countdown Days Remaining": countdown.days,
        "Countdown Hours Remaining": countdown.hours,
        Section: "Countdown Timer",
      });
      // You could add a toast notification here
      alert("Promo code WAGS50 copied to clipboard!");
    });
  };

  const handleCTAClick = (
    location,
    ctaType = "signup",
    additionalProps = {},
  ) => {
    trackEvent("WAGS Conference CTA Clicked", {
      "CTA Type": ctaType,
      "CTA Location": location,
      "Button Text": ctaType === "signup" ? "Sign Up" : "Login",
      "Promo Code Available": "WAGS50",
      "Discount Available": 50,
      "Time on Page": Math.round((Date.now() - pageLoadTime.current) / 1000),
      "Scroll Depth at Click": Math.round(
        (window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight)) *
          100,
      ),
      "Countdown Days Remaining": countdown.days,
      "Countdown Hours Remaining": countdown.hours,
      "Stats Viewed": true, // Assuming they've seen stats if they're clicking CTA
      "Video Played": isVideoPlaying,
      "Conversion Funnel Step":
        location === "header"
          ? 1
          : location === "hero"
            ? 2
            : location === "countdown"
              ? 3
              : 4,
      ...additionalProps,
    });

    if (ctaType === "signup") {
      onSignUpClick();
    } else {
      onLoginClick();
    }
  };

  const styles = {
    container: {
      fontFamily: "'Urbanist', system-ui, sans-serif",
      backgroundColor: colors.background,
      color: colors.text,
      lineHeight: 1.5,
    },
    header: {
      backgroundColor: colors.background,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      padding: "16px 0",
    },
    headerContent: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    logo: {
      fontSize: "24px",
      fontWeight: "700",
      color: colors.primary,
    },
    navLinks: {
      display: "flex",
      gap: "32px",
      alignItems: "center",
    },
    navLink: {
      color: colors.text,
      textDecoration: "none",
      fontSize: "16px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "color 0.2s ease",
    },
    btnPrimary: {
      backgroundColor: colors.primary,
      color: colors.background,
      padding: "16px 32px",
      borderRadius: "24px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.2s ease",
      textDecoration: "none",
    },
    btnSecondary: {
      backgroundColor: "transparent",
      color: colors.text,
      padding: "16px 32px",
      borderRadius: "24px",
      fontSize: "16px",
      fontWeight: "600",
      border: `1px solid ${colors.border}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    section: {
      padding: "120px 0",
    },
    sectionSm: {
      padding: "80px 0",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 24px",
    },
    card: {
      backgroundColor: colors.cardBg,
      borderRadius: "16px",
      padding: "24px",
      boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
      transition: "all 0.2s ease",
    },
    tourCard: {
      backgroundColor: colors.lightBlueBg,
      borderRadius: "16px",
      padding: "32px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      textAlign: "center",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      backgroundColor: colors.accentBg,
      color: colors.primary,
      padding: "8px 16px",
      borderRadius: "24px",
      fontSize: "14px",
      fontWeight: "500",
      marginBottom: "24px",
    },
    title: {
      fontSize: "36px",
      fontWeight: "700",
      color: colors.title,
      lineHeight: "1.2",
      marginBottom: "24px",
    },
    subtitle: {
      fontSize: "20px",
      fontWeight: "400",
      color: colors.subtext,
      lineHeight: "1.5",
      marginBottom: "32px",
    },
  };

  const ArrowRightIcon = () => (
    <svg
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9 5l7 7-7 7"
      ></path>
    </svg>
  );

  const PlayIcon = () => (
    <svg
      fill="currentColor"
      viewBox="0 0 24 24"
      style={{ width: "36px", height: "36px" }}
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );

  const ClockIcon = () => (
    <svg
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12,6 12,12 16,14"></polyline>
    </svg>
  );

  const UsersIcon = () => (
    <svg
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  );

  const StarIcon = () => (
    <svg
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
    </svg>
  );

  return (
    <div style={styles.container} className={className}>
      {/* Header */}
      {showHeader && (
        <header style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.logo}>ClinicOS</div>
            <nav style={styles.navLinks}>
              <a
                style={styles.navLink}
                onClick={() => {
                  trackEvent("WAGS Conference Header Navigation", {
                    "Navigation Item": "Features",
                    "Destination Section": "features",
                    "Navigation Method": "Click",
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                  });
                  document
                    .getElementById("features")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Features
              </a>
              <a
                style={styles.navLink}
                onClick={() => {
                  trackEvent("WAGS Conference Header Navigation", {
                    "Navigation Item": "Demo",
                    "Destination Section": "demo",
                    "Navigation Method": "Click",
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                  });
                  document
                    .getElementById("demo")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Demo
              </a>
              <a
                style={styles.navLink}
                onClick={() => {
                  trackEvent("WAGS Conference Header Navigation", {
                    "Navigation Item": "Register",
                    "Destination Section": "register",
                    "Navigation Method": "Click",
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                  });
                  document
                    .getElementById("register")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Register
              </a>
              <a
                style={styles.navLink}
                onClick={() => handleCTAClick("header", "login")}
              >
                Login
              </a>
              <button
                style={styles.btnPrimary}
                onClick={() => handleCTAClick("header", "signup")}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primaryDark;
                  e.target.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary;
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Book a Demo
              </button>
            </nav>
          </div>
        </header>
      )}

      {/* Hero Section */}
      <section
        style={{
          ...styles.section,
          marginTop: showHeader ? "80px" : "0",
          background: `linear-gradient(90deg, ${colors.lightBlueBg}, ${colors.background})`,
        }}
      >
        <div style={styles.container}>
          <div
            style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto" }}
          >
            <div style={styles.badge}>
              <span style={{ marginRight: "8px" }}>🔥</span>
              WAGS Conference 2025 - Limited Time
            </div>

            <h1 style={styles.title}>Advanced Aesthetic Simulations</h1>

            <p style={styles.subtitle}>
              Experience the future of patient consultations with AI-powered
              before/after predictions at the premier aesthetic conference.
            </p>

            <div
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                flexWrap: "wrap",
                marginBottom: "24px",
              }}
            >
              <button
                style={styles.btnPrimary}
                onClick={() => {
                  trackEvent("WAGS Conference Hero CTA", {
                    "CTA Type": "Get Early Access",
                    "Button Text": "Get Early Access - 50% Off",
                    "Discount Mentioned": 50,
                    "Promo Code Available": "WAGS50",
                    Section: "Hero",
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    "Primary CTA": true,
                  });
                  document
                    .getElementById("register")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primaryDark;
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(0,183,244,0.3)";
                  trackEvent("WAGS Conference Button Hover", {
                    Button: "Get Early Access",
                    Section: "Hero",
                  });
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                Get Early Access - 50% Off
                <ArrowRightIcon />
              </button>
              <button
                style={styles.btnSecondary}
                onClick={() => handleCTAClick("hero", "signup")}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = colors.primary;
                  e.target.style.color = colors.primary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = colors.border;
                  e.target.style.color = colors.text;
                }}
              >
                Try It Now
              </button>
            </div>

            <p style={{ fontSize: "14px", color: colors.subtext, margin: 0 }}>
              ✅ Create your first simulation to activate the discount
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={styles.sectionSm}>
        <div style={styles.container}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
            }}
          >
            {[
              {
                icon: "🎯",
                number: `${stats.professionals}+`,
                title: "Professionals Expected",
                desc: "Leading aesthetic practitioners advancing their practice",
                trend: "↗ Growing Daily",
                color: colors.success,
              },
              {
                icon: "🤖",
                number: `${stats.procedures}+`,
                title: "AI Procedures",
                desc: "Advanced simulation capabilities for every aesthetic need",
                trend: "Complete Coverage",
                color: colors.success,
              },
              {
                icon: "💰",
                number: `${stats.discount}%`,
                title: "Early Bird Discount",
                desc: "Exclusive savings for early conference registration",
                trend: "Limited Time",
                color: colors.warning,
              },
            ].map((stat, index) => (
              <div
                key={index}
                style={{
                  ...styles.card,
                  background: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "20px",
                  padding: "40px 30px",
                  textAlign: "center",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  cursor: "pointer",
                }}
                onClick={() =>
                  trackEvent("WAGS Conference Stat Card Clicked", {
                    "Stat Type": stat.title,
                    "Stat Value": stat.number,
                    "Stat Description": stat.desc,
                    "Card Position": index + 1,
                    "Total Cards": 3,
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    Section: "Stats",
                    "Engagement Type": "Card Click",
                  })
                }
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-4px)";
                  e.target.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.12)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.08)";
                }}
              >
                <div
                  style={{
                    fontSize: "30px",
                    background: "rgba(0, 183, 244, 0.1)",
                    borderRadius: "16px",
                    width: "60px",
                    height: "60px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px auto",
                  }}
                >
                  {stat.icon}
                </div>
                <div
                  style={{
                    color: colors.primary,
                    fontSize: "54px",
                    fontWeight: "700",
                    lineHeight: "1",
                    marginBottom: "20px",
                  }}
                >
                  {stat.number}
                </div>
                <div
                  style={{
                    color: colors.title,
                    fontWeight: "700",
                    fontSize: "18px",
                    marginBottom: "10px",
                  }}
                >
                  {stat.title}
                </div>
                <div
                  style={{
                    color: colors.subtext,
                    fontSize: "14px",
                    lineHeight: "1.5",
                    marginBottom: "8px",
                  }}
                >
                  {stat.desc}
                </div>
                <div style={{ fontSize: "12px", color: stat.color }}>
                  {stat.trend}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section id="demo" style={styles.sectionSm}>
        <div style={styles.container}>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div style={styles.badge}>
              <span style={{ marginRight: "8px" }}>📹</span>
              Conference Preview
            </div>
            <h2
              style={{
                fontSize: "30px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "16px",
              }}
            >
              See ClinicOS in Action
            </h2>
            <p style={{ ...styles.subtitle, margin: 0 }}>
              Watch how leading aesthetic professionals are transforming their
              practice with AI-powered simulations
            </p>
          </div>

          <div
            style={{
              backgroundColor: colors.background,
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              margin: "0 auto",
              maxWidth: "900px",
            }}
          >
            <div
              style={{
                backgroundColor: "#000",
                position: "relative",
                paddingBottom: "56.25%",
                width: "100%",
              }}
            >
              {!isVideoPlaying ? (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), 
                                linear-gradient(45deg, ${colors.lightBlueBg} 0%, ${colors.accentBg} 100%)`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                  onClick={playVideo}
                >
                  <button
                    style={{
                      width: "80px",
                      height: "80px",
                      backgroundColor: "rgba(0, 183, 244, 0.9)",
                      borderRadius: "50%",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 20px rgba(0, 183, 244, 0.4)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "scale(1.1)";
                      e.target.style.boxShadow =
                        "0 6px 30px rgba(0, 183, 244, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "scale(1)";
                      e.target.style.boxShadow =
                        "0 4px 20px rgba(0, 183, 244, 0.4)";
                    }}
                  >
                    <PlayIcon />
                  </button>
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "white",
                      fontSize: "24px",
                      fontWeight: "600",
                      textAlign: "center",
                      textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                    }}
                  >
                    ClinicOS Demo Video
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "400",
                        marginTop: "8px",
                      }}
                    >
                      Click to play demonstration
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  src={`${videoUrl}?autoplay=1`}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                />
              )}
            </div>
            <div
              style={{
                padding: "24px",
                textAlign: "center",
                backgroundColor: colors.lightBlueBg,
              }}
            >
              <h4
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: colors.title,
                  marginBottom: "8px",
                }}
              >
                Live WAGS Conference Demo
              </h4>
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "16px",
                  color: colors.text,
                }}
              >
                Experience real-time AI simulations and see how ClinicOS
                enhances patient consultations
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "24px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <ClockIcon />
                  <span style={{ fontSize: "14px", color: colors.subtext }}>
                    3:45 minutes
                  </span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <UsersIcon />
                  <span style={{ fontSize: "14px", color: colors.subtext }}>
                    Real patient demo
                  </span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <StarIcon />
                  <span style={{ fontSize: "14px", color: colors.subtext }}>
                    Live simulation
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Section */}
      <section style={styles.sectionSm}>
        <div style={styles.container}>
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <h2
              style={{
                fontSize: "30px",
                fontWeight: "600",
                color: colors.title,
              }}
            >
              Early Bird Pricing Ends Soon
            </h2>
          </div>

          <div
            style={{
              backgroundColor: colors.background,
              borderRadius: "20px",
              padding: "36px",
              border: `1px solid ${colors.border}`,
              boxShadow: "0 4px 20px rgba(0,183,244,0.08)",
              textAlign: "center",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <p
              style={{
                fontSize: "18px",
                color: colors.subtext,
                marginBottom: "30px",
              }}
            >
              Secure your 50% discount before the deadline
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                margin: "30px 0",
                flexWrap: "wrap",
              }}
            >
              {Object.entries(countdown).map(([unit, value]) => (
                <div
                  key={unit}
                  style={{
                    backgroundColor: colors.cardBg,
                    borderRadius: "12px",
                    padding: "20px 16px",
                    minWidth: "100px",
                    textAlign: "center",
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "48px",
                      fontWeight: "700",
                      color: colors.primary,
                      lineHeight: "1",
                    }}
                  >
                    {value}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: colors.subtext,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginTop: "8px",
                      fontWeight: "500",
                    }}
                  >
                    {unit}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                backgroundColor: "#E1F7FE",
                border: `2px solid ${colors.primary}`,
                borderRadius: "20px",
                padding: "24px",
                margin: "30px 0",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
              }}
              onClick={copyPromoCode}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#CCF2FF")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#E1F7FE")}
            >
              <div
                style={{
                  fontSize: "14px",
                  color: colors.subtext,
                  marginBottom: "8px",
                }}
              >
                Use Promo Code:
              </div>
              <div
                style={{
                  fontSize: "40px",
                  fontWeight: "700",
                  color: colors.primary,
                  fontFamily: "'Courier New', monospace",
                  letterSpacing: "2px",
                }}
              >
                WAGS50
              </div>
            </div>

            <button
              style={{
                ...styles.btnPrimary,
                animation: "pulse 2s infinite",
              }}
              onClick={() => handleCTAClick("countdown", "signup")}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.primaryDark;
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 4px 12px rgba(0,183,244,0.3)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.primary;
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              Sign Up & Create Simulation
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        style={{ ...styles.section, backgroundColor: colors.cardBg }}
      >
        <div style={styles.container}>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div style={styles.badge}>
              <span style={{ marginRight: "8px" }}>✨</span>
              Conference Experience
            </div>
            <h2
              style={{
                fontSize: "30px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "16px",
              }}
            >
              What You'll Discover at WAGS 2025
            </h2>
            <p
              style={{
                ...styles.subtitle,
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Explore cutting-edge AI simulation technology and revolutionary
              patient consultation workflows
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
            }}
          >
            {[
              {
                icon: "🎯",
                title: "AI-Powered Simulations",
                subtitle: "Generate realistic procedure outcomes instantly",
                description:
                  "Live demonstrations of advanced algorithms creating realistic before/after visualizations for 20+ procedures.",
              },
              {
                icon: "📱",
                title: "Complete Workflow Integration",
                subtitle: "Seamless patient journey automation",
                description:
                  "Discover streamlined patient journeys from photo capture to voice note transcription.",
              },
              {
                icon: "📊",
                title: "Analytics & ROI Insights",
                subtitle: "Track consultation metrics and effectiveness",
                description:
                  "Learn to track consultation metrics and patient engagement rates to optimize conversions.",
              },
              {
                icon: "🔒",
                title: "Enterprise Security",
                subtitle: "HIPAA-compliant with enterprise-grade security",
                description:
                  "Understand HIPAA-compliant platforms with encrypted storage and comprehensive audit trails.",
              },
              {
                icon: "⚡",
                title: "Real-Time Processing",
                subtitle: "Generate simulations in seconds",
                description:
                  "Experience generating high-quality simulations in seconds during live consultations.",
              },
              {
                icon: "🤝",
                title: "Expert Networking",
                subtitle: "Connect with leading professionals",
                description:
                  "Connect with leading aesthetic professionals and participate in exclusive case studies.",
              },
            ].map((feature, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: colors.background,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "16px",
                  padding: "32px 24px",
                  transition: "all 0.2s ease",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onClick={() =>
                  trackEvent("WAGS Conference Feature Card Clicked", {
                    "Feature Name": feature.title,
                    "Feature Description": feature.subtitle,
                    "Card Position": index + 1,
                    "Total Features": 6,
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    Section: "Features",
                    "Engagement Type": "Card Click",
                    "Feature Category": feature.icon,
                  })
                }
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-4px)";
                  e.target.style.boxShadow = "0 8px 25px rgba(0,183,244,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    borderBottom: `1px solid ${colors.border}`,
                    paddingBottom: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      width: "54px",
                      height: "54px",
                      backgroundColor: colors.accentBg,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "20px",
                      fontSize: "24px",
                    }}
                  >
                    {feature.icon}
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: colors.title,
                      marginBottom: "4px",
                    }}
                  >
                    {feature.title}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: colors.subtext,
                    }}
                  >
                    {feature.subtitle}
                  </div>
                </div>
                <p
                  style={{
                    color: colors.text,
                    lineHeight: "1.6",
                    fontSize: "16px",
                    margin: 0,
                  }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section style={styles.sectionSm}>
        <div style={styles.container}>
          <div
            style={{
              ...styles.card,
              maxWidth: "900px",
              margin: "0 auto",
              backgroundColor: colors.background,
            }}
          >
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "600",
                textAlign: "center",
                marginBottom: "32px",
                color: colors.title,
              }}
            >
              What Colleagues Are Saying
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "24px",
              }}
            >
              <div
                style={{
                  borderLeft: `4px solid ${colors.primary}`,
                  paddingLeft: "16px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  trackEvent("WAGS Conference Testimonial Clicked", {
                    "Doctor Name": "Dr. Sarah Chen",
                    "Doctor Specialty": "Plastic Surgery",
                    "Testimonial Position": 1,
                    "Total Testimonials": 2,
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    Section: "Social Proof",
                    "Engagement Type": "Testimonial Click",
                  })
                }
              >
                <p
                  style={{
                    marginBottom: "8px",
                    fontSize: "16px",
                    color: colors.text,
                  }}
                >
                  "ClinicOS has revolutionized how I present options to
                  patients. The AI simulations are incredibly accurate."
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colors.title,
                    margin: 0,
                  }}
                >
                  Dr. Sarah Chen, Plastic Surgery
                </p>
              </div>
              <div
                style={{
                  borderLeft: `4px solid ${colors.primary}`,
                  paddingLeft: "16px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  trackEvent("WAGS Conference Testimonial Clicked", {
                    "Doctor Name": "Dr. Michael Torres",
                    "Doctor Specialty": "Reconstructive Surgery",
                    "Testimonial Position": 2,
                    "Total Testimonials": 2,
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    Section: "Social Proof",
                    "Engagement Type": "Testimonial Click",
                  })
                }
              >
                <p
                  style={{
                    marginBottom: "8px",
                    fontSize: "16px",
                    color: colors.text,
                  }}
                >
                  "My consultation efficiency improved by 40%. Patients love
                  seeing the similar patient galleries."
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colors.title,
                    margin: 0,
                  }}
                >
                  Dr. Michael Torres, Reconstructive Surgery
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        id="register"
        style={{
          ...styles.section,
          backgroundColor: "#024767",
          color: "white",
        }}
      >
        <div style={styles.container}>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: "36px",
                fontWeight: "700",
                color: "white",
                marginBottom: "24px",
              }}
            >
              Reserve Your Spot Today
            </h1>
            <p
              style={{
                fontSize: "20px",
                color: "rgba(255, 255, 255, 0.9)",
                maxWidth: "600px",
                margin: "0 auto 48px",
                lineHeight: "1.5",
              }}
            >
              Join the premier conference advancing aesthetic practice with AI
              simulation technology. Limited seats available for this exclusive
              event.
            </p>

            <button
              style={{
                ...styles.btnPrimary,
                marginBottom: "48px",
                animation: "pulse 2s infinite",
              }}
              onClick={() =>
                handleCTAClick("final_cta", "signup", {
                  "Button Text": "Sign Up Now - Use Code WAGS50",
                  "Promo Code Mentioned": "WAGS50",
                  "Final CTA": true,
                  "Page Completion": "100%",
                })
              }
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.primaryDark;
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 4px 12px rgba(0,183,244,0.3)";
                trackEvent("WAGS Conference Final CTA Hover", {
                  Section: "Footer",
                  "Time on Page": Math.round(
                    (Date.now() - pageLoadTime.current) / 1000,
                  ),
                });
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.primary;
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              Sign Up Now - Use Code WAGS50
            </button>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "36px",
                flexWrap: "wrap",
                marginBottom: "32px",
              }}
            >
              <a
                style={{
                  color: "rgba(255, 255, 255, 0.8)",
                  textDecoration: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  trackEvent("WAGS Conference Footer Link Clicked", {
                    Link: "Contact Us",
                    Section: "Footer",
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    "Final CTA Seen": true,
                  })
                }
              >
                Contact Us
              </a>
              <a
                style={{
                  color: "rgba(255, 255, 255, 0.8)",
                  textDecoration: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  trackEvent("WAGS Conference Footer Link Clicked", {
                    Link: "Terms of Service",
                    Section: "Footer",
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    "Final CTA Seen": true,
                  })
                }
              >
                Terms of Service
              </a>
              <a
                style={{
                  color: "rgba(255, 255, 255, 0.8)",
                  textDecoration: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  trackEvent("WAGS Conference Footer Link Clicked", {
                    Link: "Privacy Policy",
                    Section: "Footer",
                    "Time on Page": Math.round(
                      (Date.now() - pageLoadTime.current) / 1000,
                    ),
                    "Final CTA Seen": true,
                  })
                }
              >
                Privacy Policy
              </a>
            </div>

            <p
              style={{
                color: "rgba(255, 255, 255, 0.6)",
                fontSize: "14px",
                margin: 0,
              }}
            >
              © Copyright ClinicOS 2025
            </p>
          </div>
        </div>
      </section>

      {/* CSS Animation Styles */}
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
          100% {
            transform: scale(1);
          }
        }

        @media (max-width: 991px) {
          .nav-links {
            display: none !important;
          }
        }

        @media (max-width: 767px) {
          .section {
            padding: 60px 0 !important;
          }

          .title {
            font-size: 28px !important;
          }

          .subtitle {
            font-size: 18px !important;
          }
        }

        @media (max-width: 479px) {
          .section {
            padding: 40px 0 !important;
          }

          .title {
            font-size: 24px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WAGSConferenceLanding;

// Usage examples:

// Basic usage
// <WAGSConferenceLanding />

// With custom configuration and user context
// <WAGSConferenceLanding
//   videoUrl="https://player.vimeo.com/video/YOUR_VIDEO_ID"
//   onSignUpClick={() => router.push('/signup?promo=WAGS50')}
//   onLoginClick={() => router.push('/login')}
//   showHeader={true}
//   className="custom-class"
//   clinicName={session?.clinicName}
//   userId={session?.userId}
//   referralSource="email_campaign"
// />

// Embedded without header (if you have your own navigation)
// <WAGSConferenceLanding
//   showHeader={false}
//   clinicName="Advanced Aesthetics Clinic"
//   userId="user_12345"
//   referralSource="qr_code"
//   onSignUpClick={() => {
//     // Custom signup logic
//     analytics.track('WAGS_Conference_Signup_Started');
//     window.open('/signup?source=wags&promo=WAGS50', '_blank');
//   }}
// />
